import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {OrderStore, LocationStore} from '../../store';
import {DateTimeService, IBaseResponse, WebService} from '../../services';
import dayjs, {Dayjs} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {Vue} from 'vue-property-decorator';
import type {
	ILocation,
	ILocationHours,
	IPromiseDate,
	IShift,
	TMaybeLocation,
} from '../../store';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface IAvailability {
	/**
	 * key in the form <token>-<numerical day of week>
	 *
	 * Currently, the token is hardcoded as "default" in the dayKey method.
	 * Numbers for days of the week come from Day.js's format "d" output
	 * (Sunday is 0, etc.).
	 */
	dayId: string;
	date: Dayjs;
	shifts: IShift[];
}

@Module({name: 'locationHours', namespaced: true})
class LocationHoursModule extends VuexModule {
	locationHoursInternal: Record<ILocation['id'], ILocationHours> = {};

	get location(): TMaybeLocation {
		return LocationStore.location(OrderStore.selectedLocationId);
	}

	get locationHours() {
		return (locationId: string | null = null) => {
			if(!locationId) {
				locationId = OrderStore.selectedLocationId;
			}
			if(locationId === null || !this.locationHoursInternal[locationId]) {
				return null;
			} else {
				return this.locationHoursInternal[locationId];
			}
		};
	}

	/**
	 * Given a day, this function will return the start time for the first shift of the day.
	 * If given "today", it will return the first shift that is after or during
	 * the current time in seconds after midnight (the seconds are from the store's timezone/perspective).
	 */
	get startShift() {
		return (day: Dayjs, locationId: string | null = null): IShift => {
			const locationHours = this.locationHours(locationId);
			if(!locationHours) {
				throw new Error('No location hours set!');
			}

			let shift: IShift | null;

			const dateKey = this.dateKey(day);
			if(dateKey in locationHours.exceptions) {
				shift = this.findFirstShiftForDay(day, locationHours.exceptions[dateKey].shifts);
			} else {
				const dayKey = this.dayKey(day);
				shift = this.findFirstShiftForDay(day, locationHours.defaultDays[dayKey].shifts);
			}
			if(!shift) {
				throw new Error('No shift found!');
			}
			return shift;
		};
	}

	get findFirstShiftForDay() {
		return (day: Dayjs, shifts: IShift[]) => {
			let secondsAfterMidnight = 0;
			// If today
			if(day.isSame(this.currentLocationDateTime(), 'day')) {
				// Get the current time in seconds after midnight
				secondsAfterMidnight = this.secondsAfterMidnight();
				// First, first shift that is after or during the current time
				const currentShift = shifts.find((shift) => {
					return shift.startSeconds <= secondsAfterMidnight && shift.endSeconds >= secondsAfterMidnight;
				}) || null;
				if(currentShift) {
					return currentShift;
				}
				// Else fall through to the logic below to find the next shift
			}
			// If no current shift, return the next shift
			let earliestShift: IShift | null = null;
			for(const shift of shifts) {
				if(shift.startSeconds >= secondsAfterMidnight) {
					if(!earliestShift || shift.startSeconds < earliestShift.startSeconds) {
						earliestShift = shift;
					}
				}
			}
			return earliestShift;
		};
	}

	/**
	 * returns all shifts from given list occurring during or after current location's time
	 *
	 * TODO: convert uses to DateTimeService.locationGetRemainingShiftsForDay
	 */
	get remainingShiftsForDay() {
		return (day: Dayjs, shifts: IShift[]) => {
			return DateTimeService.locationGetRemainingShiftsForDay(
				this.location,
				day,
				shifts
			);
		};
	}

	get secondsAfterMidnight() {
		// inner function prevents caching
		return (): number => DateTimeService.convertDayjsToSeconds(this.currentLocationDateTime());
	}

	/**
	 * Day key refers to the key we use for default days which use `default-$dayOfWeek`
	 */
	get dayKey() {
		return (day: Dayjs) => {
			return `default-${day.format('d')}`;
		};
	}

	/**
	 * Date key refers to the key we use for exceptions which use `D-MM-YY`
	 * (yes, no leading 0 on the day but a leading 0 on the month)
	 */
	get dateKey() {
		return (day: Dayjs) => {
			return day.format('D-MM-YY');
		};
	}

	/**
	 * returns current datetime (as Dayjs) for selected ordering location
	 */
	get currentLocationDateTime() {
		// inner function prevents caching
		return (): Dayjs => DateTimeService.locationGetLocalNow(this.location);
	}

	get nextAvailableLocationDateTime() {
		// inner function prevents caching
		return (): IPromiseDate | null => {
			let date = this.currentLocationDateTime();
			let counter = 0;
			let isNextAvailableInActiveShift = true;
			do {
				try {
					const shift = this.startShift(date);
					const secondsAfterMidnight = this.secondsAfterMidnight();

					// If we aren't in the middle of a shift, adjust the date to the start of the shift
					if(shift.startSeconds > secondsAfterMidnight) {
						date = date.startOf('day').add(shift.startSeconds, 'second');
						isNextAvailableInActiveShift = false;
					}

					// Always add the wait time if present
					return {
						date: date.add(OrderStore.location?.currentWaitTime || 0, 'minute'),
						isNextAvailableInActiveShift: isNextAvailableInActiveShift
					};
				} catch(error) {}
				counter++;
				isNextAvailableInActiveShift = false;
				date = date.add(1, 'day');
			} while(counter <= 6);

			return null;
		};
	}

	/**
	 * returns array of shifts for the next week
	 *
	 * The returned array's indices correspond to offsets from today
	 * (i.e., today = 0, tomorrow = 1). Shifts that have already ended
	 * are not included.
	 */
	get availability() {
		return (locationId: string | null = null): IAvailability[] => {
			const locationHours = this.locationHours(locationId);
			if(!locationHours) {
				return [];
			}

			const availability: IAvailability[] = [];
			const now = this.currentLocationDateTime();

			for(const index of new Array(7).keys()) {
				const nowPlusIndex = now.add(index, 'day').startOf('day');
				const dateKey = nowPlusIndex.format('YYYY-MM-DD');
				const dayKey = this.dayKey(nowPlusIndex);

				let shifts = [];
				if(dateKey in locationHours.exceptions) {
					shifts = locationHours.exceptions[dateKey].shifts;
				} else {
					shifts = locationHours.defaultDays[dayKey].shifts;
				}

				availability.push({
					dayId: dayKey,
					date: nowPlusIndex,
					shifts:	this.remainingShiftsForDay(nowPlusIndex, shifts)
				});
			}

			return availability;
		};
	}

	@Action
	async fetchLocationHours(locationId: string) {
		// This request can fire right as the app starts up so it need to wait for
		// the logged in client to be ready.
		await WebService.loggedInStarted;
		if(!this.locationHoursInternal[locationId]) {
			const response = await WebService.authenticatedClient.get<IBaseResponse<{locationHours: ILocationHours}>>(`locations/${locationId}/hours`);
			this.setLocationHours({locationId: locationId, locationHours: response.data.data.locationHours});
		}
	}

	@Mutation
	public setLocationHours(params: {locationId: string; locationHours: ILocationHours}) {
		Vue.set(this.locationHoursInternal, params.locationId, params.locationHours);
	}

	@Mutation
	public clearLocationHours() {
		this.locationHoursInternal = {};
	}
}


export {
	LocationHoursModule
};
